import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { trackDailyContentGenerateClick } from './userJourneyTracker';
import './CalendarPage.css';
import './IdeationPage.css';

const PAGE_BODY_CLASS = 'ideation-page-plain-bg';
const PENDING_SIGNIN_STORAGE_KEY = 'makereels_pending_signin';

function RefreshIcon() {
  return (
    <svg
      className="cal-btn-one-tap-social__icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="daily-content-refresh-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5170ff" />
          <stop offset="100%" stopColor="#ff66c4" />
        </linearGradient>
      </defs>
      <path
        d="M21 12a9 9 0 0 0-15-6.7M3 12a9 9 0 0 0 15 6.7M3 3v6h6M21 21v-6h-6"
        stroke="url(#daily-content-refresh-grad)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HookScoreBadge({ score }) {
  if (score == null || Number.isNaN(Number(score))) return null;
  return (
    <span className="result-virality-badge daily-content-hook-score-badge" aria-label={`Hook score ${score} out of 100`}>
      <span className="daily-content-hook-score-badge__label">Hook score</span>
      <span className="daily-content-hook-score-badge__value">
        <i className="fa-solid fa-fire-flame-curved result-virality-badge-icon" aria-hidden="true" />
        <span className="result-virality-badge-value">
          <span className="result-virality-badge-score">{score}</span>
          <span className="result-virality-badge-max">/100</span>
        </span>
      </span>
    </span>
  );
}

function PastItemHookScore({ score }) {
  if (score == null || Number.isNaN(Number(score))) return null;
  return (
    <span className="daily-content-past__item-score" aria-label={`Hook score ${score}`}>
      <i className="fa-solid fa-fire-flame-curved daily-content-past__item-score-icon" aria-hidden="true" />
      <span className="daily-content-past__item-score-value">{score}</span>
    </span>
  );
}

function PastContentOptionsMenu({ item, onDelete, isDeleting }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocPointer = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocPointer);
      document.addEventListener('touchstart', onDocPointer, { passive: true });
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [menuOpen]);

  const handleDelete = () => {
    if (isDeleting) return;
    setMenuOpen(false);
    onDelete(item.id);
  };

  return (
    <div className="cal-post-card__menu-wrap daily-content-past__menu-wrap" ref={menuRef}>
      <button
        type="button"
        className="cal-icon-btn daily-content-past__menu-btn"
        aria-label="Past content options"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={(event) => {
          event.stopPropagation();
          setMenuOpen((open) => !open);
        }}
      >
        ⋮
      </button>
      {menuOpen && (
        <div className="cal-post-card__menu daily-content-past__menu" role="menu">
          <button
            type="button"
            className="cal-post-card__menu-item"
            role="menuitem"
            disabled={isDeleting}
            onClick={handleDelete}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      )}
    </div>
  );
}

function PastContentRow({ item, isActive, isDeleting, onSelect, onDelete, formatPastLabel }) {
  const { short, when } = formatPastLabel(item);

  return (
    <div
      className={`cal-upcoming-row daily-content-past__item${isActive ? ' daily-content-past__item--active' : ''}`}
    >
      <div className="daily-content-past__item-head">
        <button
          type="button"
          className="daily-content-past__item-main"
          onClick={() => onSelect(item.id)}
        >
          <span className="daily-content-past__item-title">{short}</span>
          <span className="daily-content-past__item-meta">{when}</span>
        </button>
        <div className="daily-content-past__item-actions">
          <PastItemHookScore score={item.hook_score} />
          <PastContentOptionsMenu item={item} onDelete={onDelete} isDeleting={isDeleting} />
        </div>
      </div>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function CopyScriptButton({ script }) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  useEffect(() => () => {
    if (copyTimerRef.current) {
      window.clearTimeout(copyTimerRef.current);
    }
  }, []);

  const handleCopy = async () => {
    const text = String(script || '').trim();
    if (!text) return;

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopied(true);
      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current);
      }
      copyTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimerRef.current = null;
      }, 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className={`daily-content-copy-btn${copied ? ' daily-content-copy-btn--ok' : ''}`}
      onClick={handleCopy}
      disabled={!String(script || '').trim()}
      title={copied ? 'Copied' : 'Copy script'}
      aria-label={copied ? 'Script copied to clipboard' : 'Copy script'}
    >
      {copied ? (
        <i className="fa-solid fa-check" aria-hidden="true" />
      ) : (
        <CopyIcon />
      )}
    </button>
  );
}

function ScriptFeedbackBar({
  sentiment,
  submitting,
  onLike,
  onDislike,
}) {
  const likeIcon = sentiment === 'like' ? 'fa-solid' : 'fa-regular';
  const dislikeIcon = sentiment === 'dislike' ? 'fa-solid' : 'fa-regular';
  return (
    <div className="daily-content-feedback-bar" aria-label="Script feedback">
      <button
        type="button"
        className={`daily-content-feedback-btn${sentiment === 'like' ? ' daily-content-feedback-btn--active-like' : ''}`}
        onClick={onLike}
        disabled={submitting}
        aria-label="Like this script"
        aria-pressed={sentiment === 'like'}
      >
        <i className={`${likeIcon} fa-thumbs-up`} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`daily-content-feedback-btn${sentiment === 'dislike' ? ' daily-content-feedback-btn--active-dislike' : ''}`}
        onClick={onDislike}
        disabled={submitting}
        aria-label="Dislike this script"
        aria-pressed={sentiment === 'dislike'}
      >
        <i className={`${dislikeIcon} fa-thumbs-down`} aria-hidden="true" />
      </button>
    </div>
  );
}

function DislikeReasonPanel({ value, submitting, onChange, onCancel, onSubmit }) {
  return (
    <div className="daily-content-dislike-panel">
      <p className="daily-content-dislike-panel__title">What should we improve?</p>
      <textarea
        className="daily-content-dislike-panel__input"
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Tell us what did not work about this script…"
        disabled={submitting}
      />
      <div className="daily-content-dislike-panel__actions">
        <button type="button" className="daily-content-dislike-panel__cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className="daily-content-dislike-panel__submit"
          onClick={onSubmit}
          disabled={submitting || !value.trim()}
        >
          {submitting ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

export default function IdeationPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, getFreshToken } = useAuth();
  const [regenerating, setRegenerating] = useState(false);
  const [getNowSubmitting, setGetNowSubmitting] = useState(false);
  const [getNowRequestSent, setGetNowRequestSent] = useState(false);
  const [loadingContent, setLoadingContent] = useState(true);
  const [contentError, setContentError] = useState('');
  const [hasPrompt, setHasPrompt] = useState(false);
  const [contentDate, setContentDate] = useState('');
  const [idea, setIdea] = useState('');
  const [script, setScript] = useState('');
  const [hookScore, setHookScore] = useState(null);
  const [pastItems, setPastItems] = useState([]);
  const [loadingPast, setLoadingPast] = useState(true);
  const [selectedPastId, setSelectedPastId] = useState(null);
  const [deletingPastId, setDeletingPastId] = useState(null);
  const [todayHistoryEntryId, setTodayHistoryEntryId] = useState(null);
  const [todayFeedback, setTodayFeedback] = useState(null);
  const [todayDislikeReason, setTodayDislikeReason] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [showDislikeReason, setShowDislikeReason] = useState(false);
  const [dislikeReasonText, setDislikeReasonText] = useState('');
  const getFreshTokenRef = useRef(getFreshToken);
  const todayLoadedRef = useRef(false);

  getFreshTokenRef.current = getFreshToken;

  useEffect(() => {
    document.body.classList.add(PAGE_BODY_CLASS);
    return () => document.body.classList.remove(PAGE_BODY_CLASS);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      sessionStorage.setItem(
        PENDING_SIGNIN_STORAGE_KEY,
        'Please sign in to use Daily content idea',
      );
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const loadToday = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    setContentError('');
    if (!silent) {
      setLoadingContent(true);
    }
    try {
      const token = await getFreshTokenRef.current();
      if (!token) {
        setContentError('Please sign in again.');
        return;
      }
      const res = await fetch('/api/daily-content/today', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load daily content');
      }
      setHasPrompt(Boolean(data.has_prompt));
      setContentDate(data.date || '');
      const c = data.content;
      if (c) {
        setIdea(c.idea || '');
        setScript(c.script || '');
        setHookScore(typeof c.hook_score === 'number' ? c.hook_score : null);
        setTodayHistoryEntryId(c.history_entry_id || null);
        setTodayFeedback(c.feedback || null);
        setTodayDislikeReason(c.dislike_reason || '');
      } else {
        setIdea('');
        setScript('');
        setHookScore(null);
        setTodayHistoryEntryId(null);
        setTodayFeedback(null);
        setTodayDislikeReason('');
      }
      setShowDislikeReason(false);
      setDislikeReasonText('');
    } catch (e) {
      setContentError(e?.message || 'Failed to load daily content');
    } finally {
      if (!silent) {
        setLoadingContent(false);
      }
    }
  }, []);

  const loadPast = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setLoadingPast(true);
    }
    try {
      const token = await getFreshTokenRef.current();
      if (!token) return;
      const res = await fetch('/api/daily-content/history?limit=5', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setPastItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      /* non-blocking */
    } finally {
      if (!silent) {
        setLoadingPast(false);
      }
    }
  }, []);

  useEffect(() => {
    if (loading || !isAuthenticated) {
      todayLoadedRef.current = false;
      return;
    }
    if (todayLoadedRef.current) return;
    todayLoadedRef.current = true;
    loadToday();
    loadPast();
  }, [loading, isAuthenticated, loadToday, loadPast]);

  useEffect(() => {
    setShowDislikeReason(false);
    setDislikeReasonText('');
  }, [selectedPastId, todayHistoryEntryId]);

  const handleRegenerate = async () => {
    if (regenerating) return;
    trackDailyContentGenerateClick({
      action: idea || script ? 'regenerate' : 'generate',
    });
    setRegenerating(true);
    setContentError('');
    try {
      const token = await getFreshTokenRef.current();
      if (!token) {
        setContentError('Please sign in again.');
        return;
      }
      const res = await fetch('/api/daily-content/regenerate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data.error === 'no_prompt_assigned'
            ? 'Your content profile is not set up yet. We will notify you when it is ready.'
            : data.error === 'llm_generation_failed'
              ? 'Could not generate content right now. Please try again in a few minutes.'
              : data.error || 'Generation failed';
        throw new Error(msg);
      }
      setIdea(data.idea || '');
      setScript(data.script || '');
      setHookScore(typeof data.hook_score === 'number' ? data.hook_score : null);
      setTodayHistoryEntryId(data.history_entry_id || null);
      setTodayFeedback(null);
      setTodayDislikeReason('');
      setShowDislikeReason(false);
      setDislikeReasonText('');
      setContentDate(data.date || contentDate);
      setHasPrompt(true);
      setContentError('');
      setGetNowRequestSent(false);
      setSelectedPastId(null);
      await loadPast({ silent: true });
    } catch (e) {
      setContentError(e?.message || 'Generation failed');
    } finally {
      setRegenerating(false);
    }
  };

  const handleGetNow = async () => {
    if (getNowSubmitting || regenerating) return;
    setGetNowSubmitting(true);
    setContentError('');
    setGetNowRequestSent(false);
    try {
      const token = await getFreshTokenRef.current();
      if (!token) {
        setContentError('Please sign in again.');
        return;
      }
      const requestRes = await fetch('/api/daily-content/get-now-request', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ has_prompt: hasPrompt }),
      });
      const requestData = await requestRes.json().catch(() => ({}));
      if (!requestRes.ok) {
        throw new Error(requestData.error || 'Could not send your request');
      }

      if (hasPrompt) {
        await handleRegenerate();
        return;
      }

      setGetNowRequestSent(true);
    } catch (e) {
      setContentError(e?.message || 'Request failed');
    } finally {
      setGetNowSubmitting(false);
    }
  };

  const handleDeletePast = async (entryId) => {
    if (deletingPastId) return;
    setDeletingPastId(entryId);
    try {
      const token = await getFreshTokenRef.current();
      if (!token) return;
      const res = await fetch(`/api/daily-content/history/${encodeURIComponent(entryId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Delete failed');
      }
      const isDisplayedOnRight = entryId === displayedEntryId;
      if (isDisplayedOnRight) {
        setSelectedPastId(null);
        setIdea('');
        setScript('');
        setHookScore(null);
        setShowDislikeReason(false);
        setDislikeReasonText('');
        if (entryId === todayHistoryEntryId) {
          setTodayHistoryEntryId(null);
          setTodayFeedback(null);
          setTodayDislikeReason('');
        }
      } else if (entryId === todayHistoryEntryId) {
        setTodayHistoryEntryId(null);
        setTodayFeedback(null);
        setTodayDislikeReason('');
        setIdea('');
        setScript('');
        setHookScore(null);
      } else if (selectedPastId === entryId) {
        setSelectedPastId(null);
      }
      setPastItems((prev) => prev.filter((item) => item.id !== entryId));
    } catch {
      await loadPast({ silent: true });
    } finally {
      setDeletingPastId(null);
    }
  };

  const hasTodayContent = Boolean(idea || script);
  const generateLabel = regenerating
    ? 'Generating…'
    : hasTodayContent
      ? 'Regenerate'
      : 'Generate';

  const selectedPast = pastItems.find((item) => item.id === selectedPastId) || null;
  const viewingPast = Boolean(selectedPast);
  const activeEntryId = selectedPastId ?? todayHistoryEntryId ?? null;
  const displayedEntryId = viewingPast ? selectedPastId : todayHistoryEntryId;
  const displayIdea = viewingPast ? selectedPast.idea || '' : idea;
  const displayScript = viewingPast ? selectedPast.script || '' : script;
  const displayDate = viewingPast ? selectedPast.date || '' : contentDate;
  const displayHookScore = viewingPast ? selectedPast?.hook_score : hookScore;
  const displayTitle = viewingPast ? 'Past script' : "Today's Content Idea";
  const displayFeedback = viewingPast ? selectedPast?.feedback : todayFeedback;
  const displayHistoryEntryId = viewingPast ? selectedPast?.id : todayHistoryEntryId;
  const canSubmitFeedback = Boolean(displayHistoryEntryId && (displayIdea || displayScript));

  const submitFeedback = async ({ sentiment, dislikeReason = '' }) => {
    if (!displayHistoryEntryId || feedbackSubmitting) return;

    setFeedbackSubmitting(true);
    try {
      const token = await getFreshTokenRef.current();
      if (!token) return;
      const res = await fetch('/api/daily-content/feedback', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          history_entry_id: displayHistoryEntryId,
          sentiment,
          dislike_reason: dislikeReason,
          source: viewingPast ? 'past' : 'today',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Feedback failed');
      }
      if (viewingPast) {
        setPastItems((prev) =>
          prev.map((item) =>
            item.id === displayHistoryEntryId
              ? {
                  ...item,
                  feedback: sentiment,
                  dislike_reason: sentiment === 'dislike' ? dislikeReason : null,
                }
              : item,
          ),
        );
      } else {
        setTodayFeedback(sentiment);
        setTodayDislikeReason(sentiment === 'dislike' ? dislikeReason : '');
      }
      setShowDislikeReason(false);
      setDislikeReasonText('');
    } catch {
      /* ignore */
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleLike = () => {
    submitFeedback({ sentiment: 'like' });
  };

  const handleDislikeClick = () => {
    if (displayFeedback === 'dislike') {
      setShowDislikeReason(true);
      setDislikeReasonText(viewingPast ? selectedPast?.dislike_reason || '' : todayDislikeReason);
      return;
    }
    setShowDislikeReason(true);
    setDislikeReasonText('');
  };

  const handleDislikeSubmit = () => {
    const reason = dislikeReasonText.trim();
    if (!reason) return;
    submitFeedback({ sentiment: 'dislike', dislikeReason: reason });
  };

  const formatPastLabel = (item) => {
    const title = (item.idea || 'Untitled script').trim();
    const short = title.length > 48 ? `${title.slice(0, 48)}…` : title;
    let when = item.date || '';
    if (!when && item.generated_at) {
      when = new Date(item.generated_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } else if (when) {
      const parsed = new Date(`${when}T12:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        when = parsed.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }
    return { short, when };
  };

  const hasDisplayedContent = Boolean(displayIdea || displayScript);
  const showGetNowButton = !viewingPast && !loadingContent && !regenerating && !getNowSubmitting && !hasDisplayedContent;

  if (loading || !isAuthenticated) {
    return (
      <div className="calendar-page daily-content-page">
        <div className="calendar-page__centered">Loading…</div>
      </div>
    );
  }

  return (
    <div className="calendar-page daily-content-page">
      <div className="calendar-shell">
        <aside className="calendar-sidebar">
          <h2 className="calendar-sidebar__title">Daily content</h2>

          <div className="calendar-sidebar__actions">
            <div className="cal-schedule-btn-wrap">
              <button
                type="button"
                className="cal-btn cal-btn-one-tap-social cal-btn-full daily-content-regenerate-btn"
                onClick={handleRegenerate}
                disabled={regenerating || !hasPrompt}
                aria-busy={regenerating}
                aria-label={generateLabel}
              >
                <span className="cal-btn-one-tap-social__main">
                  <RefreshIcon />
                  <span className="cal-btn-one-tap-social__text">{generateLabel}</span>
                </span>
              </button>
            </div>
            {!hasPrompt && (
              <p className="daily-content-sidebar-hint">
                Your personalized prompt is being set up. Check back soon.
              </p>
            )}
            {hasPrompt && (
              <p className="daily-content-sidebar-hint">
                Generate new script personalised for content you create
              </p>
            )}
          </div>

          <section className="calendar-sidebar__upcoming daily-content-past">
            <h3 className="calendar-sidebar__upcoming-title">Past content</h3>
            {loadingPast && (
              <p className="daily-content-past__status">Loading…</p>
            )}
            {!loadingPast && pastItems.length === 0 && (
              <p className="daily-content-past__status">No past scripts yet.</p>
            )}
            {!loadingPast && pastItems.length > 0 && (
              <ul className="calendar-sidebar__upcoming-list daily-content-past__list">
                {pastItems.map((item) => (
                  <li key={item.id}>
                    <PastContentRow
                      item={item}
                      isActive={activeEntryId === item.id}
                      isDeleting={deletingPastId === item.id}
                      onSelect={setSelectedPastId}
                      onDelete={handleDeletePast}
                      formatPastLabel={formatPastLabel}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>

        <main className="calendar-main">
          <header className="calendar-main__header">
            <div className="calendar-main__heading">
              <h1 className="calendar-main__title">{displayTitle}</h1>
              {displayDate && (
                <span className="calendar-main__timezone">{displayDate}</span>
              )}
            </div>
            <div className="daily-content-header-actions">
              {canSubmitFeedback && (
                <ScriptFeedbackBar
                  sentiment={displayFeedback}
                  submitting={feedbackSubmitting}
                  onLike={handleLike}
                  onDislike={handleDislikeClick}
                />
              )}
              {viewingPast && (
                <button
                  type="button"
                  className="daily-content-back-today"
                  onClick={() => {
                    setSelectedPastId(null);
                    setShowDislikeReason(false);
                    setDislikeReasonText('');
                  }}
                >
                  Back to today
                </button>
              )}
            </div>
          </header>

          {showDislikeReason && canSubmitFeedback && (
            <DislikeReasonPanel
              value={dislikeReasonText}
              submitting={feedbackSubmitting}
              onChange={setDislikeReasonText}
              onCancel={() => {
                setShowDislikeReason(false);
                setDislikeReasonText('');
              }}
              onSubmit={handleDislikeSubmit}
            />
          )}

          <div className="daily-content-main__wrap">
          <div className="daily-content-main__body" aria-label="Content idea area">
            {!loadingContent && !regenerating && !contentError && hasDisplayedContent && (
              <HookScoreBadge score={displayHookScore} />
            )}
            {(loadingContent || regenerating) && !viewingPast && (
              <div className="daily-content-main__empty">
                {regenerating ? 'Generating your idea and script…' : 'Loading…'}
              </div>
            )}
            {showGetNowButton && (
              <div className="daily-content-get-now-center daily-content-get-now-center--empty">
                <p className={`daily-content-main__empty-text${contentError ? ' daily-content-main__empty-text--error' : ''}${getNowRequestSent ? ' daily-content-main__empty-text--success' : ''}`}>
                  {contentError
                    ? contentError
                    : getNowRequestSent
                      ? 'Your request has been sent. We will set up your personalised daily content profile soon.'
                      : hasPrompt
                        ? 'Get a personalised script and daily content idea, tailored for your channel on a daily basis.'
                        : 'Get a personalised script and daily content idea, tailored for your channel on a daily basis.'}
                </p>
                <button
                  type="button"
                  className="cal-btn cal-btn-one-tap-social daily-content-get-now-btn"
                  onClick={handleGetNow}
                  disabled={getNowSubmitting || regenerating || getNowRequestSent}
                  aria-busy={getNowSubmitting || regenerating}
                  aria-label="Get now"
                >
                  <span className="cal-btn-one-tap-social__main">
                    <RefreshIcon />
                    <span className="cal-btn-one-tap-social__text">
                      {getNowSubmitting || regenerating ? 'Getting…' : getNowRequestSent ? 'Request sent' : 'Get now'}
                    </span>
                  </span>
                </button>
              </div>
            )}
            {!loadingContent && !regenerating && !contentError && hasDisplayedContent && (
              <div className={`daily-content-main__filled${displayHookScore != null ? ' daily-content-main__filled--has-score' : ''}`}>
                {displayIdea && (
                  <section className="daily-content-block">
                    <h2 className="daily-content-block__label">Title</h2>
                    <p className="daily-content-block__idea">{displayIdea}</p>
                  </section>
                )}
                {displayScript && (
                  <section className="daily-content-block">
                    <div className="daily-content-block__head">
                      <h2 className="daily-content-block__label">Script</h2>
                      <CopyScriptButton script={displayScript} />
                    </div>
                    <pre className="daily-content-block__script">{displayScript}</pre>
                  </section>
                )}
              </div>
            )}
            {viewingPast && !displayIdea && !displayScript && (
              <div className="daily-content-main__empty">This script is empty.</div>
            )}
          </div>
          </div>
        </main>
      </div>
    </div>
  );
}
